// Winhouse Classroom — frontend API helper (Phase 1)
// Provider-agnostic; uses token-gated participant endpoints + admin cookie endpoints.

const BASE = process.env.REACT_APP_BACKEND_URL;

async function req(method, path, { body, query } = {}) {
  let url = `${BASE}${path}`;
  if (query) {
    const q = new URLSearchParams(query).toString();
    if (q) url += `?${q}`;
  }
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  const text = await res.text();
  try { data = text ? JSON.parse(text) : null; } catch (_e) { data = { detail: text }; }
  if (!res.ok) {
    const err = new Error((data && data.detail) || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const classroomApi = {
  // Admin
  adminStats:        ()                   => req('GET',  '/api/classroom/admin/stats'),
  adminSettings:     ()                   => req('GET',  '/api/classroom/admin/settings'),
  adminListBookings: (filter = {})        => req('GET',  '/api/classroom/admin/bookings', { query: filter }),
  adminGetBooking:   (id)                 => req('GET',  `/api/classroom/admin/bookings/${id}`),
  adminCreateBooking:(payload)            => req('POST', '/api/classroom/admin/bookings', { body: payload }),
  adminUpdateBooking:(id, payload)        => req('PATCH',`/api/classroom/admin/bookings/${id}`, { body: payload }),
  adminDeleteBooking:(id)                 => req('DELETE',`/api/classroom/admin/bookings/${id}`),
  adminResendInvite: (id, role)           => req('POST', `/api/classroom/admin/bookings/${id}/resend-invite`, { body: { role } }),
  adminAnalytics:    ()                   => req('GET',  '/api/classroom/admin/analytics'),
  adminRecording:    (id, payload)        => req('PATCH',`/api/classroom/admin/bookings/${id}/recording`, { body: payload }),

  // Participant (token-gated)
  roomContext:       (roomId, token)             => req('GET',  `/api/classroom/room/${roomId}`, { query: { t: token } }),
  attendance:        (roomId, token, event)      => req('POST', `/api/classroom/room/${roomId}/attendance`, { query: { t: token }, body: { event } }),
  endLesson:         (roomId, token)             => req('POST', `/api/classroom/room/${roomId}/end-lesson`, { query: { t: token } }),
  lessonSummary:     (roomId, token)             => req('GET',  `/api/classroom/room/${roomId}/summary`, { query: { t: token } }),
  adminForceEnd:     (roomId)                    => req('POST', `/api/classroom/admin/${roomId}/force-end`),
  adminAttendance:   (roomId)                    => req('GET',  `/api/classroom/admin/${roomId}/attendance`),
  chatList:          (roomId, token)             => req('GET',  `/api/classroom/room/${roomId}/chat`, { query: { t: token } }),
  chatPost:          (roomId, token, message)    => req('POST', `/api/classroom/room/${roomId}/chat`, { query: { t: token }, body: { message } }),
  notesGet:          (roomId, token)             => req('GET',  `/api/classroom/room/${roomId}/notes`, { query: { t: token } }),
  notesPut:          (roomId, token, payload)    => req('PUT',  `/api/classroom/room/${roomId}/notes`, { query: { t: token }, body: payload }),
  resourcesList:     (roomId, token)             => req('GET',  `/api/classroom/room/${roomId}/resources`, { query: { t: token } }),
  resourceCreateStub:(roomId, token, payload)    => req('POST', `/api/classroom/room/${roomId}/resources`, { query: { t: token }, body: payload }),
  resourceSign:      (roomId, token)             => req('POST', `/api/classroom/room/${roomId}/resources/sign`, { query: { t: token } }),
  resourceConfirm:   (roomId, token, payload)    => req('POST', `/api/classroom/room/${roomId}/resources/confirm`, { query: { t: token }, body: payload }),
  resourceDelete:    (roomId, token, resourceId) => req('DELETE',`/api/classroom/room/${roomId}/resources/${resourceId}`, { query: { t: token } }),

  // Boards (Phase 3A-1 + 3A-2)
  boardsList:        (roomId, token)             => req('GET',  `/api/classroom/room/${roomId}/boards`, { query: { t: token } }),
  boardCreate:       (roomId, token, payload)    => req('POST', `/api/classroom/room/${roomId}/boards`, { query: { t: token }, body: typeof payload === 'string' ? { name: payload, type: 'whiteboard' } : payload }),
  boardRename:       (roomId, token, bid, name)  => req('PATCH',`/api/classroom/room/${roomId}/boards/${bid}`, { query: { t: token }, body: { name } }),
  boardUpdateSource: (roomId, token, bid, url)   => req('PATCH',`/api/classroom/room/${roomId}/boards/${bid}`, { query: { t: token }, body: { source_url: url } }),
  boardDelete:       (roomId, token, bid)        => req('DELETE',`/api/classroom/room/${roomId}/boards/${bid}`, { query: { t: token } }),
  boardSnapshotSave: (roomId, token, bid, snap)  => req('PUT',  `/api/classroom/room/${roomId}/boards/${bid}/snapshot`, { query: { t: token }, body: { snapshot: snap } }),
  boardSnapshotLoad: (roomId, token, bid)        => req('GET',  `/api/classroom/room/${roomId}/boards/${bid}/snapshot`, { query: { t: token } }),

  // Classroom Settings (Phase 3A-2)
  settingsGet:       (roomId, token)             => req('GET',  `/api/classroom/room/${roomId}/settings`, { query: { t: token } }),
  settingsUpdate:    (roomId, token, payload)    => req('PUT',  `/api/classroom/room/${roomId}/settings`, { query: { t: token }, body: payload }),

  // Math Notes (Phase 3A-2)
  mathNotesList:     (roomId, token)             => req('GET',  `/api/classroom/room/${roomId}/math-notes`, { query: { t: token } }),
  mathNoteCreate:    (roomId, token, latex, label) => req('POST', `/api/classroom/room/${roomId}/math-notes`, { query: { t: token }, body: { latex, label } }),
  mathNoteDelete:    (roomId, token, noteId)     => req('DELETE',`/api/classroom/room/${roomId}/math-notes/${noteId}`, { query: { t: token } }),

  // Lesson Recap (Phase 3A-3 + 3A-4 + 3A-5)
  recapsList:        (roomId, token)             => req('GET',  `/api/classroom/room/${roomId}/recaps`, { query: { t: token } }),
  recapGenerate:     (roomId, token, payload)    => req('POST', `/api/classroom/room/${roomId}/recap/generate`, { query: { t: token }, body: payload }),
  recapAIDraft:      (roomId, token, payload)    => req('POST', `/api/classroom/room/${roomId}/recap/ai-draft`, { query: { t: token }, body: payload || {} }),
  recapDelete:       (roomId, token, rid)        => req('DELETE',`/api/classroom/room/${roomId}/recaps/${rid}`, { query: { t: token } }),
  recapDownload:     (roomId, token, rid)        => req('GET',  `/api/classroom/recap/${rid}/file`, { query: { t: token } }),
  recapSend:         (roomId, token, rid, body)  => req('POST', `/api/classroom/room/${roomId}/recaps/${rid}/send`, { query: { t: token }, body }),
  recapRecipients:   (roomId, token)             => req('GET',  `/api/classroom/room/${roomId}/recipients`, { query: { t: token } }),

  // Documents (Phase 3A-1)
  documentsList:     (roomId, token)             => req('GET',  `/api/classroom/room/${roomId}/documents`, { query: { t: token } }),
  sharedResourcesList: (roomId, token)           => req('GET',  `/api/room/${roomId}/shared-resources`, { query: { t: token } }),
  documentSign:      (roomId, token)             => req('POST', `/api/classroom/room/${roomId}/documents/sign`, { query: { t: token } }),
  documentConfirm:   (roomId, token, payload)    => req('POST', `/api/classroom/room/${roomId}/documents`, { query: { t: token }, body: payload }),
  documentDelete:    (roomId, token, did)        => req('DELETE',`/api/classroom/room/${roomId}/documents/${did}`, { query: { t: token } }),
  annotationsSave:   (roomId, token, did, page, annotations) =>
    req('PUT', `/api/classroom/room/${roomId}/documents/${did}/annotations`, { query: { t: token }, body: { page, annotations } }),
  annotationsList:   (roomId, token, did)        => req('GET',  `/api/classroom/room/${roomId}/documents/${did}/annotations`, { query: { t: token } }),

  // Email lookup (no auth)
  lookupStudent: (email) => req('POST', '/api/classroom/lookup/student', { body: { email } }),
  lookupTutor:   (email) => req('POST', '/api/classroom/lookup/tutor',   { body: { email } }),
};

export function classroomEntryUrl(roomId, token) {
  return `/classroom/${roomId}?t=${encodeURIComponent(token)}`;
}
